import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { createContext, useCallback, useContext, ReactNode } from 'react';

import { LOGOUT } from '#src/graphql/mutations/login';
import { ME } from '#src/graphql/queries/me';

export interface AuthUser {
  id: string;
  email: string;
}

interface UserContextType {
  user: AuthUser | null;
  loading: boolean;
  /** Call after successful login so the app refetches the current user (cookie is already set). */
  refetchMe: () => void;
  /** Clear session: call logout mutation and clear local state. */
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(ME, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    context: { skipAuthRefresh: true },
  });
  const [logoutMutation] = useMutation(LOGOUT, { errorPolicy: 'ignore' });

  const user = data?.me ?? null;

  const refetchMe = useCallback(() => {
    void refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation();
    await client.resetStore();
  }, [logoutMutation, client]);

  const value: UserContextType = {
    user,
    loading,
    refetchMe,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

/** By default, validates that the caller is in an authenticated session. */
export function useCurrentUser(): AuthUser;
export function useCurrentUser(required: true): AuthUser;
export function useCurrentUser(required: false): AuthUser | null;
export function useCurrentUser(required = true): AuthUser | null {
  const { user } = useUser();
  if (required != false && user == null) {
    throw new Error('useCurrentUser: no current user (required)');
  }
  return user;
}
